import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, CalendarClock, Sunrise } from "lucide-react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/Badges";
import api from "@/lib/api";
import { goldBtnSm, glassBtn, inputCls, labelCls } from "@/lib/constants";

export default function AdminOrla() {
  const [agents, setAgents] = useState([]);
  const [runs, setRuns] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState("");

  const [schedule, setSchedule] = useState(null);
  const [briefings, setBriefings] = useState([]);
  const [savingSched, setSavingSched] = useState(false);
  const [runningBrief, setRunningBrief] = useState(false);

  const load = useCallback(async () => {
    const [a, r, s, b] = await Promise.all([
      api.get("/admin/orla/agents"), api.get("/admin/orla/runs"),
      api.get("/admin/orla/schedule"), api.get("/admin/orla/briefings"),
    ]);
    setAgents(a.data); setRuns(r.data); setSchedule(s.data); setBriefings(b.data);
    if (r.data[0]) setSel((prev) => prev || r.data[0]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const trigger = async (key) => {
    setBusy(key);
    try { const { data } = await api.post("/admin/orla/run", { agent: key }); toast.success(`${data.agent_name} run complete.`); await load(); setSel(data); }
    catch (e) { toast.error("Agent run failed"); }
    finally { setBusy(""); }
  };

  const saveSchedule = async (patch) => {
    setSavingSched(true);
    try {
      const { data } = await api.put("/admin/orla/schedule", patch);
      setSchedule(data);
      toast.success("Schedule updated.");
    } catch (e) { toast.error("Could not update schedule"); }
    finally { setSavingSched(false); }
  };

  const runBriefing = async () => {
    setRunningBrief(true);
    try { await api.post("/admin/orla/run-briefing"); toast.success("Morning briefing generated & sent to admins."); await load(); }
    catch (e) { toast.error("Briefing failed"); }
    finally { setRunningBrief(false); }
  };

  const toggleAgentInSchedule = (key) => {
    if (!schedule) return;
    const agentsMap = { ...(schedule.agents || {}), [key]: !schedule.agents?.[key] };
    setSchedule({ ...schedule, agents: agentsMap });
    saveSchedule({ agents: agentsMap });
  };

  return (
    <PortalLayout title="ORLA Agents">
      {/* Scheduled runs */}
      <GlassCard className="p-6 mb-6" data-testid="orla-schedule-panel">
        <div className="flex items-center gap-3 mb-5">
          <Sunrise size={20} className="text-[hsl(var(--pdg-gold))]" strokeWidth={1.6} />
          <h2 className="pdg-serif text-2xl text-foreground">Scheduled morning briefing</h2>
        </div>
        {!schedule ? <p className="text-sm text-muted-foreground">Loading schedule…</p> : (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1 space-y-4">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-foreground/85">Enable automatic runs</span>
                <button type="button" onClick={() => saveSchedule({ enabled: !schedule.enabled })} data-testid="orla-schedule-enabled-toggle"
                  className={`w-11 h-6 rounded-full transition-colors relative ${schedule.enabled ? "bg-[hsl(var(--pdg-gold)/0.6)]" : "bg-[hsl(var(--pdg-elevated))]"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-foreground transition-all ${schedule.enabled ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </label>
              <div className="flex items-end gap-3">
                <div className="w-24">
                  <label className={labelCls}>Hour (UTC)</label>
                  <input type="number" min={0} max={23} className={inputCls} defaultValue={schedule.hour}
                    onBlur={(e) => saveSchedule({ hour: Number(e.target.value) })} data-testid="orla-schedule-hour" />
                </div>
                <div className="w-24">
                  <label className={labelCls}>Minute</label>
                  <input type="number" min={0} max={59} className={inputCls} defaultValue={schedule.minute}
                    onBlur={(e) => saveSchedule({ minute: Number(e.target.value) })} data-testid="orla-schedule-minute" />
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CalendarClock size={14} className="text-[hsl(var(--pdg-gold))]" />
                <span data-testid="orla-schedule-next-run">
                  {schedule.enabled && schedule.next_run
                    ? `Next run ${new Date(schedule.next_run).toLocaleString()}`
                    : "Automatic runs disabled"}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {schedule.last_run ? `Last briefing ${new Date(schedule.last_run).toLocaleString()}` : "No briefing run yet"}
              </div>
              <button onClick={runBriefing} disabled={runningBrief} data-testid="orla-run-briefing-button" className={goldBtnSm}>
                <Play size={13} /> {runningBrief ? "Running briefing…" : "Run briefing now"}
              </button>
            </div>

            <div className="lg:col-span-2">
              <div className="pdg-kicker mb-3">Agents included in the briefing</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {agents.map((a) => {
                  const on = schedule.agents?.[a.key];
                  return (
                    <button key={a.key} type="button" onClick={() => toggleAgentInSchedule(a.key)} disabled={savingSched}
                      data-testid={`orla-schedule-agent-${a.key}`}
                      className={`flex items-center justify-between px-4 h-11 rounded-lg border text-sm transition-colors ${on
                        ? "border-[hsl(var(--pdg-gold)/0.45)] text-foreground bg-[hsl(var(--pdg-gold)/0.06)]"
                        : "border-border text-foreground/60"}`}>
                      <span>{a.name}</span>
                      <span className={`w-2 h-2 rounded-full ${on ? "bg-[hsl(var(--pdg-gold))]" : "bg-[hsl(var(--pdg-elevated))]"}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </GlassCard>

      {/* Manual agent runs */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-6">
        {agents.map((a) => (
          <GlassCard key={a.key} className="p-5">
            <h3 className="pdg-serif text-lg text-foreground">{a.name}</h3>
            <button onClick={() => trigger(a.key)} disabled={busy === a.key} data-testid={`orla-run-${a.key}`} className={goldBtnSm + " mt-4"}>
              <Play size={13} /> {busy === a.key ? "Running…" : "Run now"}
            </button>
          </GlassCard>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <GlassCard className="p-5 lg:col-span-2" data-testid="admin-orla-run-list">
          <h2 className="pdg-serif text-xl text-foreground mb-4">Run history</h2>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {runs.map((r) => (
              <button key={r.id} onClick={() => setSel(r)} className={`w-full text-left p-3 rounded-lg border transition-colors ${sel?.id === r.id ? "border-[hsl(var(--pdg-gold)/0.4)] bg-[hsl(var(--pdg-gold)/0.06)]" : "border-border hover:border-[hsl(var(--pdg-gold)/0.25)]"}`}>
                <div className="flex items-center justify-between"><span className="text-sm text-foreground">{r.agent_name}</span><StatusBadge status={r.status} /></div>
                <div className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString()} · {r.duration_ms}ms · {r.triggered_by}</div>
              </button>
            ))}
            {runs.length === 0 && <p className="text-sm text-muted-foreground">No runs yet. Trigger an agent above.</p>}
          </div>
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-3" data-testid="admin-orla-run-detail">
          {!sel ? <p className="text-sm text-muted-foreground">Select a run to view details.</p> : (
            <div>
              <div className="flex items-center justify-between"><h2 className="pdg-serif text-2xl text-foreground">{sel.agent_name}</h2><StatusBadge status={sel.status} /></div>
              <p className="mt-4 text-sm text-foreground/85 leading-7">{sel.summary}</p>
              <div className="mt-6 pdg-kicker mb-2">Logs</div>
              <div className="pdg-mono text-xs text-muted-foreground bg-[hsl(var(--pdg-obsidian))] rounded-lg p-4 space-y-1" data-testid="admin-orla-run-log">
                {(sel.logs || []).map((l, i) => <div key={`${sel.id}-log-${i}`}>&gt; {l}</div>)}
                {(!sel.logs || sel.logs.length === 0) && <div>No log lines.</div>}
              </div>
              {sel.findings && Object.keys(sel.findings).length > 0 && (
                <div className="mt-6"><div className="pdg-kicker mb-2">Findings</div>
                  <pre className="pdg-mono text-xs text-foreground/80 bg-[hsl(var(--pdg-obsidian))] rounded-lg p-4 overflow-x-auto">{JSON.stringify(sel.findings, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Briefing history */}
      <GlassCard className="p-6 mt-6" data-testid="orla-briefings-list">
        <h2 className="pdg-serif text-2xl text-foreground mb-4">Morning briefings</h2>
        <div className="space-y-3">
          {briefings.length === 0 && <p className="text-sm text-muted-foreground">No briefings yet. Runs will appear here each morning.</p>}
          {briefings.map((b) => (
            <div key={b.id} className="p-4 rounded-xl border border-border" data-testid="orla-briefing-item">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-foreground">{new Date(b.created_at).toLocaleString()} · {b.agent_count} agents · {b.triggered_by}</div>
                <StatusBadge status={b.attention_count > 0 ? "attention" : "success"} label={b.attention_count > 0 ? `${b.attention_count} attention` : "all clear"} />
              </div>
              <div className="mt-3 space-y-1.5">
                {(b.results || []).map((r, i) => (
                  <div key={`${b.id}-${r.agent_key || r.agent_name || i}`} className="flex items-start gap-2 text-xs">
                    <StatusBadge status={r.status} />
                    <span className="text-muted-foreground leading-6"><span className="text-foreground/85">{r.agent_name}:</span> {r.summary}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </PortalLayout>
  );
}
